
import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import AdminReservations from './components/AdminReservations';
import AdminGallery from './components/AdminGallery';
import AdminReviews from './components/AdminReviews';
import AdminStats from './components/AdminStats';

export default function AdminPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('stats');
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAdminRole();
  }, [user]);

  const checkAdminRole = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      setIsAdmin(data?.role === 'admin');
    } catch (error) {
      console.error('관리자 권한 확인 오류:', error);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <i className="ri-loader-4-line text-3xl text-emerald-600 animate-spin"></i>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <i className="ri-lock-line text-6xl text-gray-400 mb-4"></i>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">로그인이 필요합니다</h2>
          <p className="text-gray-600">관리자 페이지에 접근하려면 로그인해주세요.</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <i className="ri-shield-cross-line text-6xl text-red-400 mb-4"></i>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">접근 권한이 없습니다</h2>
          <p className="text-gray-600">관리자만 접근할 수 있는 페이지입니다.</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'stats', name: '통계', icon: 'ri-bar-chart-line' },
    { id: 'reservations', name: '예약관리', icon: 'ri-calendar-line' },
    { id: 'gallery', name: '갤러리관리', icon: 'ri-image-line' },
    { id: 'reviews', name: '리뷰관리', icon: 'ri-star-line' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <i className="ri-admin-line text-2xl text-emerald-600 mr-3"></i>
              <h1 className="text-xl font-bold text-gray-900">고흥꿈트리 관리자</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                {user.user_metadata?.name || user.email}님
              </span>
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/')}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-home-line mr-1"></i>
                홈으로
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 탭 네비게이션 */}
        <div className="bg-white rounded-lg shadow-sm mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-emerald-500 text-emerald-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <i className={`${tab.icon} mr-2`}></i>
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* 탭 콘텐츠 */}
        <div className="bg-white rounded-lg shadow-sm">
          {activeTab === 'stats' && <AdminStats />}
          {activeTab === 'reservations' && <AdminReservations />}
          {activeTab === 'gallery' && <AdminGallery />}
          {activeTab === 'reviews' && <AdminReviews />}
        </div>
      </div>
    </div>
  );
}
